import { useState } from 'react';
import {
  Card, CardContent, Typography, Grid, TextField, MenuItem, Button, Stack, Alert, Divider, IconButton, Box,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { FileUploadField } from '../../../components/FileUploadField';

export interface DealerFormOption { id: number; label: string }
const DEALER_TYPES = [['distributor', 'Distributor'], ['retailer', 'Retailer'], ['franchise', 'Franchise'], ['oem_partner', 'OEM Partner']];
const DOC_TYPES = [['gst_certificate', 'GST Certificate'], ['pan_card', 'PAN Card'], ['dealership_agreement', 'Dealership Agreement'], ['address_proof', 'Address Proof'], ['other', 'Other']];

interface Doc { docType: string; fileName: string; fileUrl: string }
interface Props {
  clients?: DealerFormOption[];
  teamMembers?: DealerFormOption[];
  showLogin?: boolean;
  submitting?: boolean;
  error?: string | null;
  submitLabel?: string;
  onSubmit: (payload: Record<string, unknown>) => void;
}

const initial = {
  companyName: '', dealerType: 'distributor', brandName: '', establishmentYear: '', website: '',
  billingAddress: '', shippingAddress: '', city: '', district: '', pincode: '',
  primaryContactPerson: '', designation: '', mobile: '', alternateMobile: '', email: '',
  financeContactPerson: '', financeEmail: '', financeMobile: '', paymentTerms: 'advance', creditDays: '',
  gstNumber: '', panNumber: '', clientId: '' as number | '', salesManagerUserId: '' as number | '',
  loginEmail: '', password: '', confirmPassword: '',
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>{title}</Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>{children}</Grid>
      </CardContent>
    </Card>
  );
}

export function DealerRegistrationForm({ clients = [], teamMembers = [], showLogin = true, submitting, error, submitLabel = 'Submit Application', onSubmit }: Props) {
  const [f, setF] = useState(initial);
  const [docs, setDocs] = useState<Doc[]>([]);
  const [localErr, setLocalErr] = useState<string | null>(null);
  const set = (k: keyof typeof initial, v: unknown) => setF((s) => ({ ...s, [k]: v }));

  const submit = () => {
    setLocalErr(null);
    if (!f.companyName.trim()) return setLocalErr('Company / Dealership Name is required');
    if (f.paymentTerms === 'credit' && !f.creditDays) return setLocalErr('Credit Days is required for Credit payment terms');
    if (showLogin && f.password && f.password !== f.confirmPassword) return setLocalErr('Passwords do not match');
    const num = (v: string) => (v ? Number(v) : undefined);
    const str = (v: string) => (v.trim() ? v.trim() : undefined);
    onSubmit({
      companyName: f.companyName.trim(), dealerType: f.dealerType, brandName: str(f.brandName), establishmentYear: num(f.establishmentYear), website: str(f.website),
      billingAddress: str(f.billingAddress), shippingAddress: str(f.shippingAddress), city: str(f.city), district: str(f.district), pincode: str(f.pincode),
      primaryContactPerson: str(f.primaryContactPerson), designation: str(f.designation), mobile: str(f.mobile), alternateMobile: str(f.alternateMobile), email: str(f.email),
      financeContactPerson: str(f.financeContactPerson), financeEmail: str(f.financeEmail), financeMobile: str(f.financeMobile),
      paymentTerms: f.paymentTerms, creditDays: f.paymentTerms === 'credit' ? num(f.creditDays) : undefined,
      gstNumber: str(f.gstNumber), panNumber: str(f.panNumber),
      clientId: f.clientId || undefined, salesManagerUserId: f.salesManagerUserId || undefined,
      loginEmail: showLogin ? str(f.loginEmail) : undefined, password: showLogin && f.password ? f.password : undefined,
      documents: docs.filter((d) => d.fileName && d.fileUrl),
    });
  };

  const gi = { xs: 12, sm: 6 };
  return (
    <>
      {(error || localErr) && <Alert severity="error" sx={{ mb: 2 }}>{error || localErr}</Alert>}

      <Section title="Section 1 — Basic Information">
        <Grid item {...gi}><TextField label="Company / Dealership Name" required fullWidth value={f.companyName} onChange={(e) => set('companyName', e.target.value)} /></Grid>
        <Grid item {...gi}><TextField select label="Dealer Type" fullWidth value={f.dealerType} onChange={(e) => set('dealerType', e.target.value)}>{DEALER_TYPES.map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}</TextField></Grid>
        <Grid item {...gi}><TextField label="Brand Name" fullWidth value={f.brandName} onChange={(e) => set('brandName', e.target.value)} /></Grid>
        <Grid item {...gi}><TextField label="Establishment Year" type="number" fullWidth value={f.establishmentYear} onChange={(e) => set('establishmentYear', e.target.value)} /></Grid>
        <Grid item {...gi}><TextField label="Website" fullWidth value={f.website} onChange={(e) => set('website', e.target.value)} /></Grid>
        <Grid item {...gi}><TextField label="Business Vertical" fullWidth value="Protection Device Installation" disabled /></Grid>
      </Section>

      <Section title="Section 2 — Registered Office Details">
        <Grid item xs={12}><TextField label="Billing Address" fullWidth multiline minRows={2} value={f.billingAddress} onChange={(e) => set('billingAddress', e.target.value)} /></Grid>
        <Grid item xs={12}><TextField label="Shipping Address" fullWidth multiline minRows={2} value={f.shippingAddress} onChange={(e) => set('shippingAddress', e.target.value)} /></Grid>
        <Grid item xs={12} sm={4}><TextField label="City" fullWidth value={f.city} onChange={(e) => set('city', e.target.value)} /></Grid>
        <Grid item xs={12} sm={4}><TextField label="District" fullWidth value={f.district} onChange={(e) => set('district', e.target.value)} /></Grid>
        <Grid item xs={12} sm={4}><TextField label="Pin Code" fullWidth value={f.pincode} onChange={(e) => set('pincode', e.target.value)} /></Grid>
      </Section>

      <Section title="Section 3 — Business Contact Details">
        <Grid item {...gi}><TextField label="Primary Contact Person" fullWidth value={f.primaryContactPerson} onChange={(e) => set('primaryContactPerson', e.target.value)} /></Grid>
        <Grid item {...gi}><TextField label="Designation" fullWidth value={f.designation} onChange={(e) => set('designation', e.target.value)} /></Grid>
        <Grid item {...gi}><TextField label="Mobile Number" fullWidth value={f.mobile} onChange={(e) => set('mobile', e.target.value)} /></Grid>
        <Grid item {...gi}><TextField label="Alternate Mobile" fullWidth value={f.alternateMobile} onChange={(e) => set('alternateMobile', e.target.value)} /></Grid>
        <Grid item {...gi}><TextField label="Email ID" type="email" fullWidth value={f.email} onChange={(e) => set('email', e.target.value)} /></Grid>
      </Section>

      <Section title="Section 4 — Finance Details">
        <Grid item {...gi}><TextField label="Finance Contact Person" fullWidth value={f.financeContactPerson} onChange={(e) => set('financeContactPerson', e.target.value)} /></Grid>
        <Grid item {...gi}><TextField label="Finance Email ID" type="email" fullWidth value={f.financeEmail} onChange={(e) => set('financeEmail', e.target.value)} /></Grid>
        <Grid item {...gi}><TextField label="Finance Mobile Number" fullWidth value={f.financeMobile} onChange={(e) => set('financeMobile', e.target.value)} /></Grid>
        <Grid item {...gi}><TextField select label="Payment Terms" fullWidth value={f.paymentTerms} onChange={(e) => set('paymentTerms', e.target.value)}><MenuItem value="advance">Advance</MenuItem><MenuItem value="credit">Credit</MenuItem></TextField></Grid>
        {f.paymentTerms === 'credit' && <Grid item {...gi}><TextField label="Credit Days" type="number" fullWidth value={f.creditDays} onChange={(e) => set('creditDays', e.target.value)} /></Grid>}
      </Section>

      <Section title="Section 5 — GST & Banking Details">
        <Grid item {...gi}><TextField label="GST Number (15 chars)" fullWidth value={f.gstNumber} onChange={(e) => set('gstNumber', e.target.value.toUpperCase())} /></Grid>
        <Grid item {...gi}><TextField label="PAN Number (10 chars)" fullWidth value={f.panNumber} onChange={(e) => set('panNumber', e.target.value.toUpperCase())} /></Grid>
      </Section>

      <Section title="Section 6 — Client Mapping">
        <Grid item {...gi}><TextField select label="Client Name" fullWidth value={f.clientId} onChange={(e) => set('clientId', Number(e.target.value))}>{clients.map((c) => <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>)}</TextField></Grid>
        <Grid item {...gi}><TextField select label="Sales Manager" fullWidth value={f.salesManagerUserId} onChange={(e) => set('salesManagerUserId', Number(e.target.value))}>{teamMembers.map((m) => <MenuItem key={m.id} value={m.id}>{m.label}</MenuItem>)}</TextField></Grid>
      </Section>

      {showLogin && (
        <Section title="Section 8 — Login Credentials">
          <Grid item {...gi}><TextField label="Login Email ID" type="email" fullWidth value={f.loginEmail} onChange={(e) => set('loginEmail', e.target.value)} /></Grid>
          <Grid item {...gi} /><Grid item {...gi}><TextField label="Password" type="password" fullWidth value={f.password} onChange={(e) => set('password', e.target.value)} /></Grid>
          <Grid item {...gi}><TextField label="Confirm Password" type="password" fullWidth value={f.confirmPassword} onChange={(e) => set('confirmPassword', e.target.value)} /></Grid>
        </Section>
      )}

      <Section title="Section 9 — Document Upload">
        <Grid item xs={12}>
          <Stack spacing={1}>
            {docs.map((d, i) => (
              <Stack key={i} direction="row" spacing={1} alignItems="center">
                <TextField select size="small" label="Document" value={d.docType} onChange={(e) => setDocs((arr) => arr.map((x, j) => j === i ? { ...x, docType: e.target.value } : x))} sx={{ minWidth: 200 }}>{DOC_TYPES.map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}</TextField>
                <TextField size="small" label="File name" value={d.fileName} onChange={(e) => setDocs((arr) => arr.map((x, j) => j === i ? { ...x, fileName: e.target.value } : x))} />
                <FileUploadField value={d.fileUrl || null} onChange={(url) => setDocs((arr) => arr.map((x, j) => j === i ? { ...x, fileUrl: url ?? '' } : x))} />
                <IconButton size="small" color="error" onClick={() => setDocs((arr) => arr.filter((_, j) => j !== i))}><Delete fontSize="small" /></IconButton>
              </Stack>
            ))}
            <Box><Button size="small" startIcon={<Add />} onClick={() => setDocs((arr) => [...arr, { docType: 'gst_certificate', fileName: '', fileUrl: '' }])}>Add Document</Button></Box>
          </Stack>
        </Grid>
      </Section>

      <Stack direction="row" justifyContent="flex-end">
        <Button variant="contained" size="large" disabled={submitting} onClick={submit}>{submitLabel}</Button>
      </Stack>
    </>
  );
}
